import { httpClient, PaginationParams } from '../core/http';
import { BaseEntity } from '../types';

export interface CreateRequest {
  [key: string]: any;
}

export interface UpdateRequest {
  [key: string]: any;
}

export abstract class BaseService<
  T extends BaseEntity,
  CreateReq extends CreateRequest,
  UpdateReq extends UpdateRequest
> {
  protected basePath: string;

  constructor(basePath: string) {
    this.basePath = basePath;
  }

  async create(data: CreateReq): Promise<T> {
    const response = await httpClient.post<T>(this.basePath, data);
    return response.data;
  }

  async getById(id: number): Promise<T> {
    const response = await httpClient.get<T>(`${this.basePath}/${id}`);
    return response.data;
  }

  async getAll(params?: PaginationParams & Record<string, any>): Promise<{
    items: T[];
    total: number;
    page: number;
    size: number;
    total_pages: number;
  }> {
    const normalizedParams = { ...(params || {}) };

    if (
      normalizedParams.page_size === undefined &&
      normalizedParams.size !== undefined
    ) {
      normalizedParams.page_size = normalizedParams.size;
    }

    if (
      normalizedParams.page_size === undefined &&
      normalizedParams.limit !== undefined
    ) {
      normalizedParams.page_size = normalizedParams.limit;
    }

    if (normalizedParams.page_size === undefined) {
      normalizedParams.page_size = 10;
    }

    const response = await httpClient.get<
      | T[]
      | {
          items: T[];
          total: number;
          page: number;
          size: number;
          total_pages: number;
        }
    >(`${this.basePath}`, { params: normalizedParams });

    // Handle both array response and paginated response
    if (Array.isArray(response.data)) {
      return {
        items: response.data,
        total: response.data.length,
        page: 1,
        size: response.data.length,
        total_pages: 1,
      };
    } else {
      return response.data;
    }
  }

  async update(id: number, data: UpdateReq): Promise<T> {
    const response = await httpClient.put<T>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  async patch(id: number, data: Partial<UpdateReq>): Promise<T> {
    const response = await httpClient.patch<T>(`${this.basePath}/${id}`, data);
    return response.data;
  }
}
